// Типы данных, соответствующие backend моделям

export enum CompanyRoleType {
  Contractor = 0,
  Customer = 1,
  Both = 2
}

export interface BaseEntity {
  id: number;
  isDeleted: boolean;
}

export interface Company extends BaseEntity {
  id: number;
  fullName: string;
  shortName: string;
  inn: string;
  ogrn: string;
  kpp: string;
  legalAddress: string;
  actualAddress: string;
  role: CompanyRoleType;
  directorEmployeeId?: number;
  directorEmployee?: Employee;
  isDeleted: boolean;
}

export interface Department extends BaseEntity {
  id: number;
  name: string;
  companyId: number;
  company?: Company;
  isDeleted: boolean;
}

export interface Employee extends BaseEntity {
  id: number;
  surname: string;
  name: string;
  patronymic: string;
  birthday?: string;
  educationDocumentNumber?: string;
  departmentId: number;
  department?: Department;
  jobs?: Job[];
  employeeJobCross?: EmployeeJobCross[];
  /** Дата последнего противопожарного инструктажа */
  lastFirePreventionInstructionDate?: string | null;
  /** Дата последнего инструктажа по охране труда */
  lastLaborSafetyInstructionDate?: string | null;
  isDeleted: boolean;
}

export interface Job extends BaseEntity {
  id: number;
  name: string;
  employees?: Employee[];
  employeeJobCross?: EmployeeJobCross[];
  isDeleted: boolean;
}

export interface EmployeeJobCross extends BaseEntity {
  id: number;
  employeeId: number;
  jobId: number;
  isMain: boolean;
  employee?: Employee;
  job?: Job;
}

// API Response types
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

// Form types (для форм создания/редактирования)
export interface CompanyFormData {
  fullName: string;
  shortName: string;
  inn: string;
  ogrn: string;
  kpp: string;
  legalAddress: string;
  actualAddress: string;
  role: CompanyRoleType;
  directorEmployeeId?: number;
}

export interface DepartmentFormData {
  name: string;
  companyId: number;
}

export interface EmployeeFormData {
  surname: string;
  name: string;
  patronymic: string;
  birthday?: string;
  educationDocumentNumber?: string;
  departmentId: number;
  jobIds: number[];
  mainJobId?: number;
}

export interface JobFormData {
  name: string;
}

export interface InitiatoryInstructionItem extends BaseEntity {
  id: number;
  employeeId: number;
  responsibleEmployeeId: number;
  date: string; // ISO date string
  isDeleted: boolean;
  // Navigation properties (optional)
  employee?: Employee;
  responsibleEmployee?: Employee;
}

export interface InitiatoryInstructionItemFormData {
  employeeId: number;
  responsibleEmployeeId: number;
  date: string | Date;
}

// Simplified interface for list view to avoid circular references
export interface InitiatoryInstructionItemList {
  id: number;
  employeeId: number;
  responsibleEmployeeId: number;
  date: string;
  isDeleted: boolean;
  // No navigation properties to avoid cycles
}

export enum FirePreventionInstructionType {
  Init = 0,
  First = 1,
  Repeat = 2,
}

export interface FirePreventionInstructionItem extends BaseEntity {
  id: number;
  employeeId: number;
  responsibleEmployeeId: number;
  date: string; // ISO date string
  type: FirePreventionInstructionType;
  isDeleted: boolean;
  // Navigation properties (optional)
  employee?: Employee;
  responsibleEmployee?: Employee;
}

export interface FirePreventionInstructionItemFormData {
  employeeId: number;
  responsibleEmployeeId: number;
  date: string | Date;
  type: FirePreventionInstructionType;
}

// Simplified interface for list view to avoid circular references
export interface FirePreventionInstructionItemList {
  id: number;
  employeeId: number;
  responsibleEmployeeId: number;
  date: string;
  type: FirePreventionInstructionType;
  isDeleted: boolean;
  // No navigation properties to avoid cycles
}

export enum LaborSafetyInstructionType {
        First = 1,
        Repeat = 2,
        Unscheduled = 3,
        Targeted = 4 
}

export interface LaborSafetyInstructionItem extends BaseEntity {
  id: number;
  employeeId: number;
  responsibleEmployeeId: number;
  date: string; // ISO date string
  type: LaborSafetyInstructionType;
  reason?: string;
  isDeleted: boolean;
  // Navigation properties (optional)
  employee?: Employee;
  responsibleEmployee?: Employee;
}

export interface LaborSafetyInstructionItemFormData {
  employeeId: number;
  responsibleEmployeeId: number;
  date: string | Date;
  type: LaborSafetyInstructionType;
  reason?: string;
}

// Simplified interface for list view to avoid circular references
export interface LaborSafetyInstructionItemList {
  id: number;
  employeeId: number;
  responsibleEmployeeId: number;
  date: string;
  type: LaborSafetyInstructionType;
  reason?: string;
  isDeleted: boolean;
  // No navigation properties to avoid cycles
}

export interface ProbationInstructionItem extends BaseEntity {
  id: number;
  employeeId: number;
  responsibleEmployeeId: number;
  startDate: string; // ISO date string
  finishDate: string; // ISO date string
  docDate: string; // ISO date string
  shiftCount: number;
  withDuplication: boolean;
  numDoc: string;
  isDeleted: boolean;
  // Navigation properties (optional)
  employee?: Employee;
  responsibleEmployee?: Employee;
}

export interface ProbationInstructionItemFormData {
  employeeId: number;
  responsibleEmployeeId: number;
  startDate: string | Date;
  finishDate: string | Date;
  docDate: string | Date;
  shiftCount: number;
  withDuplication: boolean;
  numDoc: string;
}

// Simplified interface for list view to avoid circular references
export interface ProbationInstructionItemList {
  id: number;
  employeeId: number;
  responsibleEmployeeId: number;
  startDate: string;
  finishDate: string;
  docDate: string;
  shiftCount: number;
  withDuplication: boolean;
  numDoc: string;
  isDeleted: boolean;
  // No navigation properties to avoid cycles
}

// Типы для дерева документов
export enum TreeItemType {
  InstructionFolder = 0, // Папка инструктажей
  ProbationFolder = 1, // Папка стажировок
  InstructionItem = 2, // Инструктаж
  ProbationItem = 3, // Стажировка
}

export interface DocumentTreeItem {
  id: number;
  name: string;
  type: TreeItemType;
  date?: string;
  url?: string; // URL для перехода, если элемент кликабельный
  children?: DocumentTreeItem[];
  documentId?: number; // ID документа, если это лист дерева
  documentType?: string; // Тип документа (InitiatoryInstruction, ProbationInstruction и т.д.)
}

export interface EmployeeDocumentsResponse {
  employeeId: number;
  employeeName: string;
  documents: DocumentTreeItem[];
}

